import React, { useState, useRef, useEffect } from 'react'
import { useEditorStore } from '../store.js'
import { AIService } from '../ai-service.js'
import { nanoid } from '../../utils/nanoid.js'
import { toPng } from 'html-to-image'
import type { ChatMessage } from '../types.js'

export function AIChatPanel() {
  const {
    getCurrentChatMessages,
    addChatMessage,
    isAIProcessing,
    setAIProcessing,
    markup,
    setMarkup,
    createSnapshot,
    currentProject,
    createChatSession,
    switchChatSession,
    deleteChatSession,
    selectedNodeHtml,
    setSelectedNodeHtml,
    chatInput,
    setChatInput
  } = useEditorStore()

  const chatMessages = getCurrentChatMessages()

  const input = chatInput
  const setInput = setChatInput
  const [streamingText, setStreamingText] = useState('')
  const [attachedImage, setAttachedImage] = useState<{
    dataUrl: string
    width: number
    height: number
  } | null>(null)
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false)
  const [historyIndex, setHistoryIndex] = useState(-1)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const aiServiceRef = useRef<AIService | null>(null)
  const pendingMarkupRef = useRef<string | null>(null)

  // Initialize AI service once
  useEffect(() => {
    if (!aiServiceRef.current) {
      aiServiceRef.current = new AIService({
        onToolCall: (toolName, toolInput) => {
          console.log('🔧 Tool called:', toolName, toolInput)

          if (toolName === 'update_html') {
            const input = toolInput as { html: string; summary: string }
            console.log('📝 Updating HTML (length:', input.html.length, ')')
            // Update markup with complete new HTML
            pendingMarkupRef.current = input.html
            setMarkup(input.html)
          } else if (toolName === 'replace_text') {
            const input = toolInput as { old_text: string; new_text: string; summary: string }
            // Use pending markup if available (from previous tool calls in same request)
            // Otherwise use the current markup from the store
            const currentMarkup = pendingMarkupRef.current ?? useEditorStore.getState().markup

            console.log('🔍 replace_text called:')
            console.log('  Old text:', input.old_text)
            console.log('  New text:', input.new_text)
            console.log('  Summary:', input.summary)
            console.log('  Current markup:', currentMarkup)
            console.log('  Current markup length:', currentMarkup.length)
            console.log('  Using pending markup:', pendingMarkupRef.current !== null)

            const newMarkup = currentMarkup.replace(input.old_text, input.new_text)

            if (newMarkup === currentMarkup) {
              console.error('❌ replace_text: old_text not found in markup!')
              console.log('Looking for:', input.old_text.substring(0, 100))

              // Try to find what IS in the markup around this area
              const searchTerm = '<!-- Stats Cards -->'
              const index = currentMarkup.indexOf(searchTerm)
              if (index !== -1) {
                console.log('Found "<!-- Stats Cards -->" at index:', index)
                console.log('Actual markup around that area:')
                console.log(currentMarkup.substring(index, index + 200))
              } else {
                console.log('Could not even find "<!-- Stats Cards -->" comment')
              }
            } else {
              console.log('✅ replace_text: Successfully replaced text')
              console.log('  New markup length:', newMarkup.length)
            }

            console.log('📝 Setting new markup:', newMarkup)
            pendingMarkupRef.current = newMarkup
            setMarkup(newMarkup)
          }
        },
        onChunk: (text) => {
          setStreamingText(prev => prev + text)
        },
        onComplete: (fullText) => {
          setStreamingText('')
        },
        onError: (error) => {
          console.error('AI Service Error:', error)
          const errorMessage = {
            id: nanoid(),
            role: 'assistant' as const,
            content: `Error: ${error.message}`,
            timestamp: Date.now()
          }
          addChatMessage(errorMessage)
          setAIProcessing(false)
        }
      })
    }
  }, [createSnapshot, setMarkup, addChatMessage, setAIProcessing])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, streamingText])

  const handleCaptureScreenshot = async () => {
    setIsCapturingScreenshot(true)
    try {
      const canvas = document.getElementById('wireframelm-canvas-root')
      if (!canvas) {
        console.error('Canvas not found')
        return
      }

      const dataUrl = await toPng(canvas, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        skipFonts: true,
        filter: (node) => {
          // Filter out any problematic elements if needed
          return true
        }
      })

      // Load the image and resize it
      const img = new Image()
      img.onload = () => {
        // Calculate new dimensions (max width: 1024px)
        const maxWidth = 1024
        const maxHeight = 1024
        let { width, height } = img

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = Math.floor(width * ratio)
          height = Math.floor(height * ratio)
        }

        // Create a canvas to resize the image
        const resizeCanvas = document.createElement('canvas')
        resizeCanvas.width = width
        resizeCanvas.height = height
        const ctx = resizeCanvas.getContext('2d')

        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height)
          const resizedDataUrl = resizeCanvas.toDataURL('image/jpeg', 0.8)

          setAttachedImage({
            dataUrl: resizedDataUrl,
            width,
            height
          })
        }
      }
      img.src = dataUrl
    } catch (error) {
      console.error('Failed to capture screenshot:', error)
    } finally {
      setIsCapturingScreenshot(false)
    }
  }

  const handleRemoveAttachment = () => {
    setAttachedImage(null)
  }

  const handleRemoveSelectedNode = () => {
    setSelectedNodeHtml(null)
  }

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items
    if (!items) return

    // Look for image in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item && item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (!file) continue

        const reader = new FileReader()
        reader.onload = async (event) => {
          const dataUrl = event.target?.result as string
          if (!dataUrl) return

          // Load image to resize it
          const img = new Image()
          img.onload = () => {
            let width = img.width
            let height = img.height

            // Resize if needed
            const maxWidth = 1024
            const maxHeight = 1024
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height)
              width = Math.floor(width * ratio)
              height = Math.floor(height * ratio)
            }

            // Create a canvas to resize the image
            const resizeCanvas = document.createElement('canvas')
            resizeCanvas.width = width
            resizeCanvas.height = height
            const ctx = resizeCanvas.getContext('2d')

            if (ctx) {
              ctx.drawImage(img, 0, 0, width, height)
              const resizedDataUrl = resizeCanvas.toDataURL('image/jpeg', 0.8)

              setAttachedImage({
                dataUrl: resizedDataUrl,
                width,
                height
              })
            }
          }
          img.src = dataUrl
        }
        reader.readAsDataURL(file)
        break
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle Enter key for sending
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
      return
    }

    // Get user messages only
    const userMessages = chatMessages.filter(msg => msg.role === 'user')

    // Handle arrow up - go to previous message
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (userMessages.length === 0) return

      const newIndex = historyIndex + 1
      if (newIndex < userMessages.length) {
        setHistoryIndex(newIndex)
        setInput(userMessages[userMessages.length - 1 - newIndex]?.content || '')
      }
    }

    // Handle arrow down - go to next message (or back to empty)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex <= 0) {
        setHistoryIndex(-1)
        setInput('')
      } else {
        const newIndex = historyIndex - 1
        setHistoryIndex(newIndex)
        if (newIndex >= 0) {
          setInput(userMessages[userMessages.length - 1 - newIndex]?.content || '')
        } else {
          setInput('')
        }
      }
    }
  }

  const handleSend = async () => {
    if (!input.trim()) {
      return
    }

    const userMessage: ChatMessage = {
      id: nanoid(),
      role: 'user' as const,
      content: input,
      timestamp: Date.now(),
      ...(attachedImage && { imageAttachment: attachedImage })
    }

    addChatMessage(userMessage)
    setInput('')
    setAttachedImage(null)
    setSelectedNodeHtml(null)
    setHistoryIndex(-1)
    setAIProcessing(true)

    // Reset pending markup ref for new request
    pendingMarkupRef.current = null

    try {
      // Create snapshot of current state before AI makes changes
      createSnapshot(input)

      const sendOptions: any = {
        userMessage: input,
        chatHistory: chatMessages,
        currentMarkup: markup
      }

      if (attachedImage) {
        sendOptions.imageAttachment = attachedImage
      }

      if (selectedNodeHtml) {
        sendOptions.selectedNodeHtml = selectedNodeHtml
      }

      const aiMessage = await aiServiceRef.current!.sendMessage(sendOptions)

      addChatMessage(aiMessage)

      // Create snapshot after AI completes (will capture the new markup if it changed)
      // Use setTimeout to ensure markup state has been updated
      setTimeout(() => {
        if (aiMessage.toolCalls && aiMessage.toolCalls.length > 0) {
          const toolCall = aiMessage.toolCalls[0]
          if (toolCall) {
            const summary = (toolCall.input as { summary?: string }).summary
            createSnapshot(undefined, summary)
          }
        }
      }, 100)
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setAIProcessing(false)
    }
  }


  const currentChatSession = currentProject?.chatSessions.find(
    s => s.id === currentProject.currentChatSessionId
  )

  const handleNewChat = () => {
    createChatSession()
  }

  const handleSwitchChat = (sessionId: string) => {
    switchChatSession(sessionId)
  }

  const handleDeleteChat = (sessionId: string) => {
    if (currentProject && currentProject.chatSessions.length > 1) {
      deleteChatSession(sessionId)
    }
  }

  // Show warning if no project is selected
  if (!currentProject) {
    return (
      <aside className="ai-chat-panel">
        <div className="ai-chat-panel__header">
          <h3 className="ai-chat-panel__title">AI Assistant</h3>
        </div>
        <div className="ai-chat-panel__messages">
          <div className="ai-chat-panel__empty">
            <p>Please create or select a project to start chatting with the AI assistant.</p>
          </div>
        </div>
      </aside>
    )
  }

  return (
    <aside className="ai-chat-panel">
      <div className="ai-chat-panel__header">
        <div className="ai-chat-panel__header-left">
          <h3 className="ai-chat-panel__title">AI Assistant</h3>
          {currentProject.chatSessions.length > 1 && currentChatSession && (
            <select
              className="ai-chat-panel__session-select"
              value={currentChatSession.id}
              onChange={(e) => handleSwitchChat(e.target.value)}
            >
              {currentProject.chatSessions.map(session => (
                <option key={session.id} value={session.id}>
                  {session.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="ai-chat-panel__header-right">
          <button
            type="button"
            className="ai-chat-panel__new-chat-button"
            onClick={handleNewChat}
            title="Start a new chat"
          >
            + New Chat
          </button>
        </div>
      </div>

      <div className="ai-chat-panel__messages">
        {chatMessages.length === 0 ? (
          <div className="ai-chat-panel__empty">
            <p>Ask me to help you create or modify wireframes.</p>
            <p className="ai-chat-panel__hint">Try: "Create a login form with email and password fields"</p>
          </div>
        ) : (
          <>
            {chatMessages.map(message => (
              <div key={message.id} className={`ai-chat-message ai-chat-message--${message.role}`}>
                {message.imageAttachment && (
                  <div className="ai-chat-message__image">
                    <img
                      src={message.imageAttachment.dataUrl}
                      alt="Attached screenshot"
                      className="ai-chat-message__image-preview"
                    />
                  </div>
                )}
                <div className="ai-chat-message__content">{message.content}</div>
                {message.toolCalls && message.toolCalls.length > 0 && (
                  <div className="ai-chat-message__tools">
                    {message.toolCalls.map(toolCall => (
                      <div key={toolCall.id} className="ai-chat-message__tool">
                        <span className="ai-chat-message__tool-name">{toolCall.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {streamingText && (
              <div className="ai-chat-message ai-chat-message--assistant ai-chat-message--streaming">
                <div className="ai-chat-message__content">
                  {streamingText}
                  <span className="ai-chat-message__cursor">▊</span>
                </div>
              </div>
            )}
            {isAIProcessing && !streamingText && (
              <div className="ai-chat-message ai-chat-message--assistant ai-chat-message--streaming">
                <div className="ai-chat-message__content">
                  <span className="ai-chat-message__thinking">Thinking</span>
                  <span className="ai-chat-message__cursor">▊</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      <div className="ai-chat-panel__input">
        {selectedNodeHtml && (
          <div className="ai-chat-panel__attachment-preview">
            <div className="ai-chat-panel__selected-node">
              <div className="ai-chat-panel__selected-node-label">Selected element:</div>
              <code className="ai-chat-panel__selected-node-code">
                {selectedNodeHtml.length > 100
                  ? selectedNodeHtml.substring(0, 100) + '...'
                  : selectedNodeHtml}
              </code>
            </div>
            <button
              type="button"
              className="ai-chat-panel__attachment-remove"
              onClick={handleRemoveSelectedNode}
              title="Remove selected element"
            >
              ×
            </button>
          </div>
        )}
        {attachedImage && (
          <div className="ai-chat-panel__attachment-preview">
            <img
              src={attachedImage.dataUrl}
              alt="Screenshot preview"
              className="ai-chat-panel__attachment-image"
            />
            <button
              type="button"
              className="ai-chat-panel__attachment-remove"
              onClick={handleRemoveAttachment}
              title="Remove attachment"
            >
              ×
            </button>
          </div>
        )}
        <textarea
          className="ai-chat-panel__textarea"
          placeholder="Describe what you want to create... (paste images to attach)"
          value={input}
          onChange={e => {
            setInput(e.target.value)
            // Reset history index when user types
            if (historyIndex !== -1) {
              setHistoryIndex(-1)
            }
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={3}
        />
        <div className="ai-chat-panel__actions">
          <button
            type="button"
            className="ai-chat-panel__screenshot-button"
            onClick={handleCaptureScreenshot}
            disabled={isAIProcessing || isCapturingScreenshot}
            title="Attach screenshot of current preview"
          >
            {isCapturingScreenshot ? '...' : '+ Screenshot'}
          </button>
          <button
            type="button"
            className="ai-chat-panel__send-button"
            onClick={handleSend}
            disabled={!input.trim() || isAIProcessing}
          >
            {isAIProcessing ? 'Processing...' : 'Send'}
          </button>
        </div>
      </div>
    </aside>
  )
}
